// What the form's field rows need to know about scheduling, without every one
// of them being handed two more props — t-159.
//
// A context rather than prop-drilling because the marks belong to about thirty
// rows spread over eight sections, several of them nested two levels inside a
// reveal block. The provider is the form itself, so nothing outside it can see
// a mark; in create mode it publishes `null` and every mark disappears, which
// is the right answer — a board being created has no cycle to wait for.

import { createContext, useContext } from "react";

import type { PendingPackage } from "./types";

export interface PendingMarks {
  /** The package waiting on this board, if there is one still waiting. An
     applied or refused one marks nothing: its values are on the board already,
     or they never will be. */
  pending: PendingPackage | null;
  /** Whether the "when may this field move" grammar is drawn at all. False in
     create mode, where every field is being set for the first time. */
  marked: boolean;
  /**
   * Whether this board ever cuts another cycle.
   *
   * False takes the "next cycle only" pill off every field that wears it: on a
   * board with no cut those fields are not waiting for a moment, they are
   * frozen for good — and the pill would sit beside a hint reading "Fixed
   * after creation" and a save bar saying they cannot be edited at all.
   *
   * Only that pill. `ext_key` is locked and `status` applies immediately
   * whatever the window does, and both marks stay.
   */
  cuts: boolean;
}

const EMPTY: PendingMarks = { pending: null, marked: false, cuts: false };

export const PendingMarksCtx = createContext<PendingMarks>(EMPTY);

export function usePendingMarks(): PendingMarks {
  return useContext(PendingMarksCtx);
}

/** The value the package holds for a field, or `undefined` when it holds none.
   `guards.max_score_per_submit` asks about the `guards` column, because that
   is the key a package carries. */
export function pendingValueOf(
  marks: PendingMarks,
  field: string,
): unknown | undefined {
  const pkg = marks.pending;
  if (!pkg || pkg.status !== "pending") return undefined;
  const [column, ...rest] = field.split(".");
  if (!(column in pkg.fields)) return undefined;
  const value = pkg.fields[column];
  if (rest.length === 0) return value;
  const nested = value as Record<string, unknown> | null;
  return nested?.[rest.join(".")];
}
